import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

import debounce from 'shared-utils/src/debounce';
import sleep from 'shared-utils/src/sleep';
import trackGTMEvents from 'shared-utils/src/trackGTMEvents/new';
import { getItem } from 'shared-utils/src/localStorage';

import {
  polylineStyle,
  polygonStyle,
  DRAWING_ACTIONS_MAP,
  TRAVEL_COPY_MAP,
  LS_FAVORITEADDRESSES,
  NO_LABEL,
} from '../../../constants';

import {
  getHighestPoint,
  getLowestPoint,
  mouseCoordsToLatLng,
  setMarkerContent,
  getBounds,
  getPolygonBounds,
  latLngToPx,
  pxToLatLng,
  addressBaloon,
  getBboxPolygon,
} from '../../../helpers/gmaps';

import removeActiveShapes from '../../../helpers/removeActiveShapes';

import { getAddressNumber } from '../../../helpers/getAddress';

import useSaveAddress from '../../../helpers/useSaveAddress';
import useEditSavedAddress from '../../../helpers/useEditSavedAddress';

import Overlayer from '../../layout/Overlayer';

import Controls from './Controls';

const NearbyTool = ({
  initialized,
  map,
  container,
  libraries,
  drawingData,
  searchMode,
  setSearchMode,
  device,
  mobileOs,
  viewport,
  getAvailableResults,
  setDrawingData,
  setNearby,
  columnsAreCollapsed,
  updateQuery,
  openQbFiltersPanel,
  qbGoToSRP,
  controlsVariant,
  qbSSearchAction,
  setSaveSearchDrawingData,
  isSafari,
  showYouGotAwayMsg,
  setShowYouGotAwayMsg,
  filters,
  getDrawingData,
  synchPreviousStepsActiveShape,
  setInitialShapeForPrevious,
  deleteDraftAndStopSearchMode,
  toggleDeleteDraftAndStopSearchMode,
  isShowSaveAddressDialogOpen,
  showSaveAddressDialog,
  isHandleAddressesModalOpen,
  showHandleAddressesModal,
  setPinpoint,
  setShapeAddressBaloon,
  setShapeHasBeenDeleted,
}) => {
  const [isDrawing, setIsDrawing] = useState(!drawingData || drawingData.action !== DRAWING_ACTIONS_MAP.nearby);
  const [hasDrawing, setHasDrawing] = useState(drawingData && drawingData.action === DRAWING_ACTIONS_MAP.nearby);

  const [c, setCenter] = useState(null);
  const [travelTime, setTravelTime] = useState(drawingData?.travelTime ? drawingData?.travelTime.toString() : null);
  const [travelMode, setTravelMode] = useState(drawingData?.travelMode || null);
  const [availableResults, setAvailableResults] = useState(drawingData?.availableResults || { loading: false, value: 0 });
  const [availablePoi, setAvailablePoi] = useState(null);
  const [polygonPath, setPolygonPath] = useState(drawingData?.nearbyPolygon || null);
  const [centerFromInit] = useState(drawingData?.nearbyCenter || null);
  const [pathFromInit] = useState(drawingData?.nearbyPolygon || null);

  const hasClickedRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const clickCoordsRef = useRef(null);
  const moveCoordsRef = useRef({ x: 0, y: 0 });

  const polylineRef = useRef(null);
  const polygonRef = useRef(null);  

  const northernMostVertexRef = useRef(null);
  const centerMarkerIcon = useRef(null);
  const centerMsgMarker = useRef(null);
  const deleteMarkerRef = useRef(null);
  const editMarkerRef = useRef(null);
  const addressBaloonRef = useRef(null);

  const filtersToAddToQuery = useRef({});

  const startEditingRef = useRef(false);

  const confirmedRef = useRef(false);
  const createdFromSSearch = useRef(false);

  const availableResultsRef = useRef(availableResults);
  const filtersRef = useRef(filters);

  const mapBoundsOnCreateShape = useRef(null);

  // creo una referenza interna di searchMode attivo per il listener del deleteCirle
  // che altrimenti non recepisce il possibile cambio di valore della prop
  const searchModeRef = useRef(searchMode);

  // referenze interne per il marker del centro cosi da renderlo cliccabile
  const polygonPathRef = useRef(drawingData?.nearbyPolygon || null);
  const nearbySearchAddressRef = useRef(drawingData?.nearbySearchAddress || null);
  const travelModeRef = useRef(travelMode);
  const travelTimeRef = useRef(travelTime);
  const cRef = useRef(c);

  const updateAvailableResults = (arObj) => {
    availableResultsRef.current = arObj;
    setAvailableResults(arObj);
  };

  const updateSearchAddressRef = (addr) => {
    nearbySearchAddressRef.current = addr;
  }

  const setDrawingDataAddress = (nearbySearchAddress) => {
    let newDData = {
      ...drawingData,
      nearbySearchAddress,
    };

    const newDDataKeys = Object.keys(newDData);

    if (newDDataKeys.length === 1 && newDDataKeys[0] === 'nearbySearchAddress' && !newDDataKeys.nearbySearchAddress) {
      newDData = null;
    }

    updateSearchAddressRef(nearbySearchAddress);

    setDrawingData(newDData);
  };

  const deletePolygon = () => {
    if (polylineRef.current) {
      // cancelliamo la polyline
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    if (polygonRef.current) {
      // cancelliamo il poligono
      polygonRef.current.setMap(null);
      polygonRef.current = null; 
    }

    if (deleteMarkerRef.current) {
      deleteMarkerRef.current.setMap(null);
      deleteMarkerRef.current = null;
    }

    if (centerMarkerIcon.current) {
      centerMarkerIcon.current.setMap(null);
      centerMarkerIcon.current = null;
    }

    if (editMarkerRef.current) {
      editMarkerRef.current.setMap(null);
      editMarkerRef.current = null;
    }

    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }

    if (addressBaloonRef.current) {
      addressBaloonRef.current.setMap(null);
      addressBaloonRef.current = null;
    }

    setHasDrawing(false);
    setTravelMode(null);
    setTravelTime(null);
    travelModeRef.current = null;
    travelTimeRef.current = null;
    setCenter({ delete: true });
    cRef.current = { delete: true };

    if (!searchModeRef.current) {
      const action = 'delete';
      const deleteShapedata = { ...getDrawingData(), fromBack: true };

      setDrawingData(null);
      if (drawingData && drawingData.action === DRAWING_ACTIONS_MAP.nearby && drawingData.fromSSearch) {
        setSaveSearchDrawingData(null);
      }

      const newBounds = map.getBounds();

      updateQuery({
        geobounds: {
          bbox: getBounds(newBounds),
        },
        bboxPolygon: getBboxPolygon(newBounds),
        nearby: null,
        q: null,
        resetToMapLocation: true,
      }, action, deleteShapedata);

      setShapeHasBeenDeleted(false);
    } else {
      setTimeout(() => setIsDrawing(true));
    }
  };

  const getDeleteMarkerCoords = ({ path }) => {
    // calcoliamo l'indice del vertice più a nord all'interno dell'array di tutti i vertici che compongono il path
    northernMostVertexRef.current = getHighestPoint(polylineRef.current);
    // prendiamo le sue coordinate
    return path[northernMostVertexRef.current];
  };

  const createDeleteMarker = ({ coords }) => {
    const { marker: { AdvancedMarkerElement } } = libraries;

    deleteMarkerRef.current = new AdvancedMarkerElement({
      map,
      position: { lat: coords.lat(), lng: coords.lng() },
      content: setMarkerContent({ cancel: true }),
      zIndex: 1,
    });

    // al click sul deleteMarker
    deleteMarkerRef.current.addListener('click', deletePolygon);
  };

  // creiamo il marker che mostra il numero di risultati disponibili
  const createCenterMsgMarker = ({ polygonCenter, availableResultsObj = null, animated = false }) => {
    const { marker: { AdvancedMarkerElement } } = libraries;

    const ar = availableResultsObj || availableResultsRef.current;

    const mrkContent = animated
      ? { centerMsg: true, animated: true }
      : { centerMsg: `${ar.value} risultati`, animated: false };
    
    centerMsgMarker.current = new AdvancedMarkerElement({
      map,
      position: {
        lat: polygonCenter.lat(),
        lng: polygonCenter.lng(),
      },
      content: setMarkerContent(mrkContent),
      zIndex: 502,
    });
    centerMsgMarker.current.addListener('click', async () => {
      if (qbGoToSRP) {
        qbGoToSRP({
          tempLocations: {
            searchLocations: [],
            searchLocationsParent: [],
          },
          shapeFilter: {
            nearby: {
              location: { lat: polygonCenter.lat(), lon: polygonCenter.lng() },
              travel_mode: travelModeRef.current || travelMode,
              travel_time: parseInt(travelTimeRef.current || travelTime, 10),
            },
            q: null,
            geobounds: null,
            geopolygon: null,
            geocircle: null,
          },
          forceMap: true,
        });
      } else {
        confirmNearby({ 
          nearbySearchAddress: nearbySearchAddressRef.current,
          path: polygonPathRef.current, 
          ar: availableResultsObj || availableResultsRef.current,
          clickFrom: 'QB_MapLabel',
          center: polygonCenter,
        });
      }
    });
  };

  const setEditingMode = () => {
    if (editMarkerRef.current) {
      const { core: { LatLng } } = libraries;

      editMarkerRef.current.setMap(null);
      editMarkerRef.current = null;
    
      // settiamo lo stile corretto alla polyline
      polylineRef.current.setOptions({ ...polylineStyle.editing });
      polygonRef.current.setOptions({ ...polygonStyle.drawing });

      const hasCenter = c && !c.delete;

      const previousCenter = centerFromInit
        ? { lat: centerFromInit.lat, lng: centerFromInit.lon }
        : { lat: drawingData?.nearbyCenter?.lat, lng: drawingData?.nearbyCenter?.lon };

      const polygonCenter = hasCenter ? c : new LatLng(previousCenter);

      createCenterMsgMarker({ polygonCenter });

      if (!hasCenter) {
        setCenter(polygonCenter);
        cRef.current = polygonCenter;
      }

      // comunichiamo che siamo in fase di editing
      startEditingRef.current = true;
      setSearchMode(DRAWING_ACTIONS_MAP.nearby);
    }
  };

  const createEditMarker = ({ revertDrawingData = null, forceRDDUse = false }) => {
    const { marker: { AdvancedMarkerElement } } = libraries;
    // troviamo il vertice più 'basso' della figura e stampiamo il l'editMarker 
    const lowestVertexCoords = polylineRef.current.getPath().getAt(getLowestPoint(polylineRef.current));

    const tT = ((revertDrawingData && revertDrawingData?.travelTime && !travelTimeRef.current) || forceRDDUse) ? revertDrawingData.travelTime : travelTimeRef.current || travelTime;
    const tM = ((revertDrawingData && revertDrawingData?.travelMode && !travelModeRef.current) || forceRDDUse) ? revertDrawingData.travelMode : travelModeRef.current || travelMode;

    editMarkerRef.current = new AdvancedMarkerElement({
      map,
      position: { lat: lowestVertexCoords.lat(), lng: lowestVertexCoords.lng() },
      content: setMarkerContent({ edit: true, value: `a ${tT}' ${TRAVEL_COPY_MAP[tM]}` }),
      zIndex: 1,
    });

    // al click sull'editMarker
    editMarkerRef.current.addListener('click', setEditingMode);
  };

  const createOrUpdatePolygon = ({
    path,
    availableResultsObj,
    mode,
    time,
  }) => {
    const { core: { LatLngBounds, LatLng } } = libraries;

    const latLngPath = path.map(x => new LatLng({
      lat: x[1],
      lng: x[0],
    }));

    if (deleteMarkerRef.current) {
      deleteMarkerRef.current.setMap(null);
      deleteMarkerRef.current = null;
    }

    if (!polylineRef.current) {
      const { maps: { Polyline, Polygon } } = libraries;
      polylineRef.current = new Polyline(polylineStyle.editing);
      polylineRef.current.setMap(map);
      
      polygonRef.current = new Polygon(polygonStyle.drawing);
      polygonRef.current.setMap(map);
    }
    
    polylineRef.current.setPath(latLngPath);
    polygonRef.current.setPath(latLngPath);

    setPolygonPath(latLngPath);
    polygonPathRef.current = latLngPath;

    const dCoords = getDeleteMarkerCoords({ path: latLngPath });
    createDeleteMarker({ coords: dCoords });

    const bounds = getPolygonBounds([polygonRef.current], LatLngBounds);
    if (!centerMsgMarker.current && !editMarkerRef.current) {
      createCenterMsgMarker({
        polygonCenter: c,
        availableResultsObj,
      });
    } else if (centerMsgMarker.current) {
      centerMsgMarker.current.position = {
        lat: c.lat(),
        lng: c.lng(),
      };
      centerMsgMarker.current.content = setMarkerContent({ centerMsg: `${availableResultsObj.value} risultati`, animated: false });
    }
    
    map.fitBounds(bounds);

    // su mobile spostiamo più in alto il centro per evitare parte del poligono venga coperta dal layer di scelta
    if (device === 'smartphone') {
      const mCenter = latLngToPx(map.getCenter(), map); 
      const correctPanCoords = pxToLatLng(mCenter[0], mCenter[1] + 45, map) || map.getCenter();
      map.panTo(correctPanCoords);
    }

    if (editMarkerRef.current && confirmedRef.current) {
      editMarkerRef.current.setMap(null);
      editMarkerRef.current = null;
      createEditMarker({ revertDrawingData: { travelMode: mode, travelTime: time }, forceRDDUse: true });
    }

    confirmedRef.current = false;

    updateAvailableResults(availableResultsObj);
    setIsDrawing(false);
    setHasDrawing(true);
  };

  const fetchAR = async ({ pos, mode, time }) => {    
    setAvailableResults({ loading: true, value: 0 });
    if (centerMsgMarker.current) {
      centerMsgMarker.current.content = setMarkerContent({ centerMsg: true, animated: true });
    }
    
    const ar = await getAvailableResults({
      newLocations: [],
      newFilters: {
        ...filtersRef.current,
        nearby: {
          location: {
            lat: pos.lat(),
            lon: pos.lng(),
          },
          travel_mode: mode,
          travel_time: parseInt(time, 10),
        },
        q: null,
        geocircle: null,
        geobounds: null,
        geopolygon: null,
        qExpansion: null,
      },
    });
        
    setAvailablePoi(ar.availablePoi);
    if (ar.nearbyGeoJSON) {
      createOrUpdatePolygon({
        path: ar.nearbyGeoJSON.coordinates[0],
        availableResultsObj: { loading: false, value: ar.total },
        mode,
        time,
      });
    } else {
      updateAvailableResults({ loading: false, value: ar.total });
    }
  };

  const updateTravelMode = (val) => {
    setTravelMode(val);
    travelModeRef.current = val;
    if (travelTime) {
      fetchAR({
        pos: c,
        mode: val,
        time: travelTime,
      });
    }
  };

  const updateTravelTime = (val) => {
    setTravelTime(val);
    travelTimeRef.current = val;
    if (travelMode) {
      fetchAR({
        pos: c,
        mode: travelMode,
        time: val,
      });
    }
  };

  const confirmNearby = ({
    nearbySearchAddress,
    path,
    ar,
    center: fwCenter,
    clickFrom = 'QB_MainButton',
  }) => {
    const { core: { LatLngBounds } } = libraries;

    confirmedRef.current = true;
    
    createEditMarker({});
    
    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }

    // aggiorniamo lo stile della polyline
    polylineRef.current.setOptions({
      ...polylineStyle.complete,
    });

    polygonRef.current.setOptions({
      ...polygonStyle.complete,
    });

    const newDrawingData = {
      nearbySearchAddress,
      availableResults: ar,
      action: DRAWING_ACTIONS_MAP.nearby,
      nearbyPolygon: path.map(node => [node.lng(), node.lat()]),
      nearbyCenter: {
        lat: c?.lat() || fwCenter?.lat(),
        lon: c?.lng() || fwCenter?.lng(),
      },
      travelMode: travelModeRef.current || travelMode,
      travelTime: travelTimeRef.current ? parseInt(travelTimeRef.current, 10) : parseInt(travelTime, 10),
      activeShapes: [
        polylineRef.current,
        polygonRef.current,
        deleteMarkerRef.current,
        editMarkerRef.current,
        centerMarkerIcon.current,
      ],
      shapeBounds: getPolygonBounds([polygonRef.current], LatLngBounds),
      from: DRAWING_ACTIONS_MAP.nearby,
    };

    setDrawingData(newDrawingData);
    setPinpoint(null);

    newDrawingData.mapBoundsOnCreateShape = mapBoundsOnCreateShape.current;
    if (filtersRef.current.q) {
      newDrawingData.q = filtersRef.current.q;
    }

    setNearby({
      nearbyData: {
        location: {
          lat: c?.lat() || fwCenter?.lat(),
          lon: c?.lng() || fwCenter?.lng(),
        },
        travel_mode: travelModeRef.current || travelMode,
        travel_time: travelTimeRef.current ? parseInt(travelTimeRef.current, 10) : parseInt(travelTime, 10),
      },
      nearbyFilters: filtersToAddToQuery.current,
      action: 'draw',
      drawingData: newDrawingData,
      // nearbySearchAddress,
    });

    if (showYouGotAwayMsg) {
      setShowYouGotAwayMsg(false);
    }

    sleep(150).then(() => {
      trackGTMEvents({
        category: 'Interaction',
        action: 'SeeSearchResults',
        // searchType: 'nearby',  
        position: clickFrom,
      }, {}, true);
      setSearchMode(null);
    });
  };
 
  // aggiungiamo i filtri settati via QB alla query
  const updateFiltersToAddToMapQuery = ({ filtersQB, searchAddress, ar }) => {
    filtersToAddToQuery.current = { ...filtersToAddToQuery.current, ...filtersQB };
    setAvailableResults(ar);
    confirmNearby({ 
      nearbySearchAddress: searchAddress, 
      path: polygonPath, 
      ar, 
      clickFrom: 'QB_MainButton',
    });
  };

  const saveAddressSuccessAction = ({ favorite }) => {
    const [fav] = favorite;
    const { address, number, town, label, lat, lon } = fav;
    const baloonData = {
      address,
      location: {
        lat,
        lng: lon,
      },
      name: label && label !== NO_LABEL ? label : `${address}${number ? ` ${number}` : ''}`,
      number: number || '',
      parent: label && label !== NO_LABEL ? `${address}${number ? ` ${number}` : ''}, ${town}` : town,
      favorite: true,
    };

    addressBaloonRef.current.content = addressBaloon(baloonData, fav, manageFavoriteAddresses);
  };

  const editAddressSuccessAction = ({ pinConfiguration }) => {
    if (addressBaloonRef.current) {
      let baloonData = {
        name: pinConfiguration.name,
        parent: pinConfiguration.parent,
        favorite: pinConfiguration.favorite,
      };
      let favData = null;
      let action = saveAddress;
      if (pinConfiguration.favorite) {
        const latlngStr = getLatLngStr(pinConfiguration.lat, pinConfiguration.lon);
        const favArr = getItem(LS_FAVORITEADDRESSES) || [];
        favData = favArr.filter(f => latlngStr === getLatLngStr(f.lat, f.lon))[0];
        action = manageFavoriteAddresses;
      } else {
        const { address, number } = getAddressNumber(pinConfiguration);
        baloonData = {
          ...baloonData,
          address,
          number: number || '',
          location: {
            lat: pinConfiguration.lat,
            lng: pinConfiguration.lon,
          },
        };
      }

      addressBaloonRef.current.content = addressBaloon(baloonData, favData, action);
    }
  };

  const { saveAddress } = useSaveAddress({ saveAddressSuccessAction });
  const { manageFavoriteAddresses } = useEditSavedAddress({ map, libraries, editAddressSuccessAction });

  const getLatLngStr = (x, y) => `${x}-${y}`;

  const handleCenterMarkerClick = () => {
    if (!searchModeRef.current) {
      if (addressBaloonRef.current) {
        addressBaloonRef.current.setMap(null);
        addressBaloonRef.current = null;
      } else {
        const { core: { LatLng }, marker: { AdvancedMarkerElement } } = libraries;

        const { name, parent, number } = nearbySearchAddressRef.current;

        const lat = centerMarkerIcon.current.position.lat;
        const lng = centerMarkerIcon.current.position.lng;

        const { address, number: computedNumber } = getAddressNumber(nearbySearchAddressRef.current);
        const num = number || computedNumber;
  
        const [x, y] = latLngToPx(new LatLng(lat, lng), map);
        const mCoords = pxToLatLng(x, y - 32, map);

        let favorite = false;
        let favData = null;
        let baloonData = {
          address,
          location: {
            lat,
            lng,
          },
          name,
          number: num || '',
          parent,
          favorite: false,
        }

        const favArr = getItem(LS_FAVORITEADDRESSES) || [];
        if (favArr.length) {
          const latlngStr = getLatLngStr(lat, lng);
          favData = favArr.filter(f => latlngStr === getLatLngStr(f.lat, f.lon))[0];          
          if (favData) {
            favorite = true;
            baloonData.name = favData.label && favData.label !== NO_LABEL ? favData.label : `${favData.address}${favData.number ? ` ${favData.number}` : ''}`;
            baloonData.parent = favData.label && favData.label !== NO_LABEL ? `${favData.address}${favData.number ? ` ${favData.number}` : ''}, ${favData.town}` : favData.town;
            baloonData.favorite = true;
          }
        }

        const action = favorite ? manageFavoriteAddresses : saveAddress;

        addressBaloonRef.current = new AdvancedMarkerElement({
          map,
          position: mCoords,
          content: addressBaloon(baloonData, favData, action),
          zIndex: 503,
        });
      }

      setShapeAddressBaloon(addressBaloonRef.current);
    }
  };

  // creiamo i 2 marker del centro del cerchio
  const createCenterMarker = ({ circleCenter }) => {
    const { marker: { AdvancedMarkerElement } } = libraries;

    centerMarkerIcon.current = new AdvancedMarkerElement({
      map,
      position: {
        lat: circleCenter.lat(),
        lng: circleCenter.lng(),
      },
      content: setMarkerContent({ center: true, handle: false }),
      gmpDraggable: false,
    });

    centerMarkerIcon.current.addListener('click', handleCenterMarkerClick);
  };

  const setNearbyCenter = ({ center, doSetCenter = true }) => {
    if (doSetCenter) {
      setCenter(center);
      cRef.current = center;
    }
    if (centerMarkerIcon.current) {
      centerMarkerIcon.current.setMap(null);
      centerMarkerIcon.current = null;
    }
    createCenterMarker({ circleCenter: center });
    mapBoundsOnCreateShape.current = map.getBounds();
    map.panTo(center);
    map.setZoom(15);
  };

  const deleteNearbyCenter = () => {
    if (centerMarkerIcon.current) {
      centerMarkerIcon.current.setMap(null);
      centerMarkerIcon.current = null;
    }
  };

  const createInitialPolygon = ({
    path,
    center,
    fromSSearch = false,
    fromBack = false,
  }) => {
    const {
      core: { LatLngBounds, LatLng },
      maps: { Polyline, Polygon },
    } = libraries;    

    const latLngPath = path.map(x => new LatLng({
      lat: x[1],
      lng: x[0],
    }));

    if (fromSSearch) {
      polylineRef.current.setMap(null);
      polygonRef.current.setMap(null);
      deleteMarkerRef.current.setMap(null);
      editMarkerRef.current.setMap(null);
      centerMarkerIcon.current.setMap(null);
      polylineRef.current = null;
      polygonRef.current = null;
      deleteMarkerRef.current = null;
      editMarkerRef.current = null;
      centerMarkerIcon.current = null;

      setSaveSearchDrawingData(null);
      createdFromSSearch.current = false;
    }

    polylineRef.current = new Polyline(polylineStyle.complete);
    polylineRef.current.setPath(latLngPath);
    polylineRef.current.setMap(map);
    
    polygonRef.current = new Polygon(polygonStyle.complete);
    polygonRef.current.setPath(latLngPath);
    polygonRef.current.setMap(map);

    const initialCenter = new LatLng({ lat: center.lat, lng: center.lon });
    setCenter(initialCenter);
    cRef.current = initialCenter;
    setPolygonPath(latLngPath);
    polygonPathRef.current = latLngPath;

    createCenterMarker({ circleCenter: initialCenter });

    const dCoords = getDeleteMarkerCoords({ path: latLngPath });
    createDeleteMarker({ coords: dCoords });

    createEditMarker(fromSSearch ? { revertDrawingData: drawingData, forceRDDUse: true } : {});

    const polygonBounds = getPolygonBounds([polygonRef.current], LatLngBounds);

    const activeShapes = [
      polylineRef.current,
      polygonRef.current,
      deleteMarkerRef.current,
      editMarkerRef.current,
      centerMarkerIcon.current,
    ];

    const newDrawingData = {
      ...drawingData,
      fromSSearch: false,
      activeShapesToRemove: null,
      activeShapes,
      shapeBounds: polygonBounds,
      from: DRAWING_ACTIONS_MAP.nearby,
    }

    setDrawingData(newDrawingData);

    if (fromBack) {
      synchPreviousStepsActiveShape(activeShapes);

      setNearby({
        nearbyData: {
          location: {
            lat: initialCenter.lat(),
            lon: initialCenter.lng(),
          },
          travel_mode: travelMode,
          travel_time: parseInt(travelTime, 10),
        },
        nearbyFilters: filtersToAddToQuery.current,
        // nearbySearchAddress,
      });
      // setBoundsChangedOnBack(true);
      map.fitBounds(polygonBounds);
    } else {
      setInitialShapeForPrevious(newDrawingData);
    }

    if (drawingData?.from === DRAWING_ACTIONS_MAP.nearby) {
      return;
    }
    map.fitBounds(polygonBounds);
  };

  const revertNearby = () => {
    const {
      core: { LatLngBounds, LatLng },
      maps: { Polyline, Polygon },
    } = libraries;  

    const latLngPath = drawingData.nearbyPolygon.map(x => new LatLng({
      lat: x[1],
      lng: x[0],
    }));

    if (!polylineRef.current) {
      polylineRef.current = new Polyline(polylineStyle.complete);
      polygonRef.current = new Polygon(polygonStyle.complete);
    } else {
      polylineRef.current.setOptions({ ...polylineStyle.complete });
    }
    
    polylineRef.current.setPath(latLngPath);
    polylineRef.current.setMap(map);
    
    polygonRef.current.setPath(latLngPath);
    polygonRef.current.setMap(map);

    setTravelMode(drawingData.travelMode);
    setTravelTime(drawingData.travelTime.toString());
    travelModeRef.current = drawingData.travelMode;
    travelTimeRef.current = drawingData.travelTime;
    setAvailableResults(drawingData.availableResults);

    const dCoords = getDeleteMarkerCoords({ path: latLngPath });
    if (deleteMarkerRef.current) {
      deleteMarkerRef.current.position = { lat: dCoords.lat(), lng: dCoords.lng() };
    } else {
      createDeleteMarker({ coords: dCoords });
    }

    let revertCenter = false;
    if (
      !centerMarkerIcon.current
      || (
        centerMarkerIcon.current
        && (
          centerMarkerIcon.current.position.lat !== drawingData.nearbyCenter.lat
          || centerMarkerIcon.current.position.lng !== drawingData.nearbyCenter.lng 
        )
      )
    ) {
      if (centerMarkerIcon.current) {
        centerMarkerIcon.current.setMap(null);
        centerMarkerIcon.current = null;
      }
      const nCenter = new LatLng({ lat: drawingData.nearbyCenter.lat, lng: drawingData.nearbyCenter.lon });
      createCenterMarker({ circleCenter: nCenter });
      setCenter(nCenter);
      cRef.current = nCenter;
      revertCenter = true;
    }

    if (centerMsgMarker.current) {
      centerMsgMarker.current.setMap(null);
      centerMsgMarker.current = null;
    }

    if (!editMarkerRef.current) {
      createEditMarker({ revertDrawingData: drawingData });
    }
    
    const bounds = getPolygonBounds([polygonRef.current], LatLngBounds);
    revertCenter && map.fitBounds(bounds);

    setIsDrawing(false);
    setHasDrawing(true);
    setSearchMode(null);
    setDrawingData({
      ...drawingData,
      activeShapes: [
        polylineRef.current,
        polygonRef.current,
        deleteMarkerRef.current,
        editMarkerRef.current,
        centerMarkerIcon.current,
      ],
      shapeBounds: bounds,
    });
  };

  // eventi legati al click sulla mappa quando si entra in modalità disegno
  const touchStartListenerDebounced = debounce((e) => {
    // se l'utente fa uno swipe a un dito
    if (e.touches.length === 1) {
      // abilitiamo il disegno
      hasClickedRef.current = true;
      // salviamo le coordinate a cui è avvenuto il click
      clickCoordsRef.current = { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };      
    }
    // altrimenti sappiamo che sta zoomando o facendo pan sulla mappa
  }, 25);

  const handleMouseDown = useCallback((e) => {
    // per i dispositivi touch usiamo un metodo debounced per metterci in ascolto delle two fingers gestures
    if (e.touches && !!e.touches[0]) {

      touchStartListenerDebounced(e);
      return;
    }
    if (!polylineRef.current) {
      const { maps: { Polyline, Polygon } } = libraries;
      polylineRef.current = new Polyline(polylineStyle.editing);
      polylineRef.current.setMap(map);
      
      polygonRef.current = new Polygon(polygonStyle.drawing);
      polygonRef.current.setMap(map);
    }
    // abilitiamo il disegno
    hasClickedRef.current = true;
    // salviamo le coordinate a cui è avvenuto il click
    clickCoordsRef.current = { clientX: e.clientX, clientY: e.clientY };
  }, [map]);

  const handleMouseMove = useCallback((e) => {
    // solo se l'utente muove il puntatore o il dito senza aver rilasciato il click
    if (hasClickedRef.current) {
      // registriamo che l'utente sta muovendo il puntatore
      hasDraggedRef.current = true;
      // consideriamo l'evento corretto a seconda del device
      const correctEvt = e.touches && !!e.touches[0]
        ? e.touches[0]
        : e;
      // salviamo l'ampienza della gesture che sta compiendo
      moveCoordsRef.current = { x: Math.abs(correctEvt.clientX - clickCoordsRef.current.clientX), y: Math.abs(correctEvt.clientY - clickCoordsRef.current.clientY) };
    }
  }, [map]);

  const handleMouseUp = useCallback(() => {
    // se l'utente non ha mosso il puntatore o se lo ha mosso di <= 10px in qualsiasi direzione
    // consideriamo l'evento come un click e partiamo col disegno
    if (hasClickedRef.current && (!hasDraggedRef.current || (moveCoordsRef.current.x <= 10 && moveCoordsRef.current.y <= 10))) {
      // ricaviamo l'oggetto latLng dal punto in cui è avvenuto il click
      const clickedLatLngPoint = mouseCoordsToLatLng(clickCoordsRef.current, map, device, isSafari);
      setNearbyCenter({ center: clickedLatLngPoint });
    }
    
    hasClickedRef.current = false;
    hasDraggedRef.current = false;
    clickCoordsRef.current = null;
    moveCoordsRef.current = { x: 0, y: 0 };
  }, [map, device, isSafari]);

  useEffect(() => {    
    // quando l'utente inizia a disegnare
    if (isDrawing) {
      if (device !== 'desktop') {
        container.current.addEventListener('touchstart', handleMouseDown);
        container.current.addEventListener('touchmove', handleMouseMove);
        container.current.addEventListener('touchend', handleMouseUp);
      } else {
        map && map.setOptions({ draggableCursor: 'crosshair' });

        container.current.addEventListener('mousedown', handleMouseDown);
        container.current.addEventListener('mousemove', handleMouseMove);
        container.current.addEventListener('mouseup', handleMouseUp);
      }
    // una volta che l'utente ha completato il disegno del cerchio
    } else if (device !== 'desktop') {
      if (container.current) {
        container.current.removeEventListener('touchstart', handleMouseDown);
        container.current.removeEventListener('touchmove', handleMouseMove);
        container.current.removeEventListener('touchend', handleMouseUp);
      }
    } else {
      map && map.setOptions({ draggableCursor: null });
      if (container.current) {
        container.current.removeEventListener('mousedown', handleMouseDown);
        container.current.removeEventListener('mousemove', handleMouseMove);
        container.current.removeEventListener('mouseup', handleMouseUp);
      }
    }
    
    return () => {
      if (container.current) {
        if (device !== 'desktop') {
          if (container.current) {
            container.current.removeEventListener('touchstart', handleMouseDown);
            container.current.removeEventListener('touchmove', handleMouseMove);
            container.current.removeEventListener('touchend', handleMouseUp);
          }
        } else {
          map && map.setOptions({ draggableCursor: null });
          if (container.current) {
            container.current.removeEventListener('mousedown', handleMouseDown);
            container.current.removeEventListener('mousemove', handleMouseMove);
            container.current.removeEventListener('mouseup', handleMouseUp);
          }
        }
      }
    };
  }, [
    libraries,
    map,
    container,
    isDrawing,
    device,
  ]);

  useEffect(() => {
    if (initialized && pathFromInit && centerFromInit) {
      createInitialPolygon({
        center: centerFromInit,
        path: pathFromInit,
        fromBack: drawingData?.fromBack,
      });
    }
  }, [initialized, pathFromInit, centerFromInit]);

  useEffect(() => {
    if (drawingData) {
      if (drawingData.action !== DRAWING_ACTIONS_MAP.nearby && (drawingData.activeShapes || []).length) {
        removeActiveShapes({ drawingData });
      } else if (drawingData.action === DRAWING_ACTIONS_MAP.nearby && (drawingData.activeShapesToRemove || []).length && !createdFromSSearch.current) {
        createdFromSSearch.current = true;
        const fromSSearch = true;
        removeActiveShapes({ drawingData, fromSSearch });
        createInitialPolygon({
          center: drawingData.nearbyCenter,
          path: drawingData.nearbyPolygon,
          fromSSearch,
        });
      } else if (drawingData.action === DRAWING_ACTIONS_MAP.nearby && drawingData.availableResults?.value !== availableResults.value) {
        updateAvailableResults({ ...availableResults, value: drawingData.availableResults?.value });
      }
    }
  }, [drawingData]);

  // aggiorno la referenza interna di searchMode
  useEffect(() => {
    searchModeRef.current = searchMode;
    if (searchMode === DRAWING_ACTIONS_MAP.nearby && polylineRef.current && !startEditingRef.current) {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      
      // cancelliamo l'editMarker
      if (editMarkerRef.current) {
        editMarkerRef.current.setMap(null);
        editMarkerRef.current = null;
      }

      // cancelliamo il deleteMarkerRef
      if (deleteMarkerRef.current) {
        deleteMarkerRef.current.setMap(null);
        deleteMarkerRef.current = null;
      }

      if (centerMarkerIcon.current) {
        centerMarkerIcon.current.setMap(null);
        centerMarkerIcon.current = null;
      }

      setIsDrawing(true);
      setHasDrawing(false);
      setTravelTime(null);
      setTravelMode(null);
      travelModeRef.current = null;
      travelTimeRef.current = null;
      setCenter({ delete: true });
      cRef.current = { delete: true };
    } else if (startEditingRef.current) {
      startEditingRef.current = false;
    }
  }, [searchMode]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (deleteDraftAndStopSearchMode) {
      if (drawingData && drawingData.action === DRAWING_ACTIONS_MAP.nearby) {
        revertNearby();
      } else {
        deletePolygon();
      }
      setSearchMode(null);
      toggleDeleteDraftAndStopSearchMode();
    }
  }, [deleteDraftAndStopSearchMode]);

  return initialized && (
    <Overlayer
      isOpen={!!searchMode}
      columnsAreCollapsed={columnsAreCollapsed}
    >
      <Controls
        map={map}
        searchMode={searchMode}
        setSearchMode={setSearchMode}
        hasDrawing={hasDrawing}
        mobileOs={mobileOs}
        device={device}
        libraries={libraries}
        center={c}
        drawingData={drawingData}
        setDrawingDataAddress={setDrawingDataAddress}
        setNearbyCenter={setNearbyCenter}
        deleteNearbyCenter={deleteNearbyCenter}
        availableResults={availableResults}
        updateFiltersToAddToMapQuery={updateFiltersToAddToMapQuery}
        confirmNearby={confirmNearby}
        travelTime={travelTime}
        setTravelTime={updateTravelTime}
        travelMode={travelMode}
        setTravelMode={updateTravelMode}
        polygonPath={polygonPath}
        deletePolygon={deletePolygon}
        revertNearby={revertNearby}
        viewportWidth={viewport?.width || 0}
        openQbFiltersPanel={openQbFiltersPanel}
        qbGoToSRP={qbGoToSRP}
        controlsVariant={controlsVariant}
        qbSSearchAction={qbSSearchAction}
        availablePoi={availablePoi}
        isShowSaveAddressDialogOpen={isShowSaveAddressDialogOpen}
        showSaveAddressDialog={showSaveAddressDialog}
        isHandleAddressesModalOpen={isHandleAddressesModalOpen}
        showHandleAddressesModal={showHandleAddressesModal}
        updateSearchAddressRef={updateSearchAddressRef}
      />
    </Overlayer>  
  );
};

export default NearbyTool;

NearbyTool.propTypes = {
  initialized: PropTypes.bool,
  map: PropTypes.instanceOf(Object),
  container: PropTypes.instanceOf(Object),
  searchMode: PropTypes.string,
  setSearchMode: PropTypes.func,
  mobileOs: PropTypes.string,
  device: PropTypes.string,
  libraries: PropTypes.instanceOf(Object),
  drawingData: PropTypes.instanceOf(Object),
  columnsAreCollapsed: PropTypes.bool,
  updateQuery: PropTypes.func,
  getAvailableResults: PropTypes.func,
  setDrawingData: PropTypes.func,
  setNearby: PropTypes.func,
  viewport: PropTypes.instanceOf(Object),
  openQbFiltersPanel: PropTypes.func,
  qbGoToSRP: PropTypes.func,
  controlsVariant: PropTypes.string,
  qbSSearchAction: PropTypes.func,
  setSaveSearchDrawingData: PropTypes.func,
  isSafari: PropTypes.bool,
  showYouGotAwayMsg: PropTypes.bool,
  setShowYouGotAwayMsg: PropTypes.func,
  setPinpoint: PropTypes.func,
  setShapeAddressBaloon: PropTypes.func,
};

NearbyTool.defaultProps = {
  initialized: false,
  map: null,
  container: null,
  searchMode: null,
  setSearchMode: PropTypes.func,
  mobileOs: '',
  device: '',
  libraries: null,
  drawingData: null,
  columnsAreCollapsed: false,
  updateQuery: () => {},
  getAvailableResults: () => {},
  setDrawingData: () => {},
  setNearby: () => {},
  viewport: {},
  openQbFiltersPanel: null,
  qbGoToSRP: null,
  controlsVariant: null,
  qbSSearchAction: null,
  setSaveSearchDrawingData: () => {},
  isSafari: false,
  showYouGotAwayMsg: false,
  setShowYouGotAwayMsg: () => {},
  setPinpoint: () => {},
  setShapeAddressBaloon: () => {},
};
